// useAlerts.js
// Subscribes to the alerts collection — returns unconsumed alerts so
// AlertQueue can fire them one at a time.
//
// Query: where(consumed == false) only. Expiry and ordering are handled
// client-side because adding `expiresAt > now` + `orderBy(expiresAt)` to the
// server query would require a composite index, and the alerts collection
// stays small enough that client-side filtering is trivial.
//
// Returns:
//   pending  — unconsumed alerts (filtered to non-expired, sorted oldest first)
//   debug    — { authed, listening, lastError, totalDocs } for diagnostics
import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { auth, db } from "./../firebase";

export default function useAlerts() {
  const [authed, setAuthed]       = useState(!!auth.currentUser);
  const [pending, setPending]     = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [listening, setListening] = useState(false);
  const [lastError, setLastError] = useState(null);

  useEffect(() => {
    // Wait for the persisted session to restore before going anonymous —
    // signInAnonymously at mount races the restore and replaces a signed-in
    // staff session for the whole browser profile (auth persistence is
    // shared per-origin, including overlay-preview iframes).
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u || auth.currentUser) { setAuthed(true); return; }
      signInAnonymously(auth)
        .then(() => console.log("[useAlerts] anonymous sign-in OK"))
        .catch((err) => {
          console.error("[useAlerts] anonymous sign-in FAILED", err);
          setLastError(`Auth: ${err.code || err.message}`);
        });
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!authed) return;
    console.log("[useAlerts] subscribing to alerts collection");
    const q = query(collection(db, "alerts"), where("consumed", "==", false));
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (!listening) setListening(true);
        const now = Date.now();
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTotalDocs(all.length);
        const live = all
          .filter((a) => {
            const exp = a.expiresAt?.toMillis?.() ?? 0;
            return exp > now;
          })
          .sort(
            (a, b) =>
              (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0)
          );
        console.log(`[useAlerts] snapshot: ${all.length} total · ${live.length} live`);
        setPending(live);
        setLastError(null);
      },
      (err) => {
        console.error("[useAlerts] snapshot error", err);
        setLastError(`Snapshot: ${err.code || err.message}`);
        setListening(false);
      }
    );
    return () => { console.log("[useAlerts] unsubscribing"); unsub(); };
  }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  return { pending, debug: { authed, listening, lastError, totalDocs } };
}
